import {
  ForwardedRef,
  forwardRef,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import {
  Controller,
  ControllerFieldState,
  ControllerRenderProps,
  FieldPath,
  FieldValues,
  useFormContext,
} from 'react-hook-form';

import { useMergeRefs } from '@xemplo/ref-utils';

import { NumberFormatter } from './helpers/number-formatter';
import { NumberField } from './number-field';
import { EventType, NumberFieldRhfProps } from './number-field.types';

type FieldProps<
  TFieldValues extends FieldValues = FieldValues,
  TName extends FieldPath<TFieldValues> = FieldPath<TFieldValues>,
> = {
  field: ControllerRenderProps<TFieldValues, TName>;
  fieldState: ControllerFieldState;
};

/**
 * TextFieldRhf is a React component that integrates the TextField with React Hook Form.
 * It uses the Controller component to manage the form state and validation.
 *
 * @param props - The properties for the TextFieldRhf component.
 * @returns A TextField component wrapped with React Hook Form's Controller.
 */
export const NumberFieldRhf = forwardRef(
  (props: NumberFieldRhfProps, ref?: ForwardedRef<HTMLInputElement>) => {
    const { name, rules } = props;
    const { control } = useFormContext();
    const renderField = useMemo(() => fieldFactory(props, ref), [props, ref]);

    return (
      <Controller
        name={name}
        control={control}
        rules={rules}
        shouldUnregister={rules?.shouldUnregister}
        render={renderField}
      />
    );
  },
);

const NonKeystrokeEvents: readonly EventType[] = [
  EventType.Wheel,
  EventType.KeyDown,
  EventType.Stepper,
];

/**
 * In order to be able to use the `useMergeRefs`, had to extract the component render.
 * This should ensure correctness of the of the react-hook-form integration.
 *
 * NOTE: Handling the value formatting and unformatting directly in the field render
 * to ensure that the display value is always formatted correctly.
 * Dealing with this just with events were causing race conditions and crazy re-renders.
 *
 * End up using a hidden input that is what actually connects to the React Hook Form.
 * This way, the visible input is always formatted for display, while the hidden input
 * holds the raw value that React Hook Form uses for validation and submission.
 **/
const fieldFactory = (
  props: NumberFieldRhfProps,
  ref?: ForwardedRef<HTMLInputElement>,
) => {
  const { rules, ...inputProps } = props;
  return ({ field, fieldState }: FieldProps) => {
    const mergedRefs = useMergeRefs([field.ref, ref]);
    const isKeystrokeRef = useRef(false);
    // Rebuilt each render rather than cached in a ref, so a runtime format switch
    // — e.g. the deduction Amount going currency → percent when the calculation
    // method changes — re-masks the field. The constructor is trivial (it stores
    // the options; the Intl formatters are created per format() call), so this
    // allocates nothing extra. Its per-render identity is a deliberate dependency
    // of the effect below — that is what re-masks the field — so exhaustive-deps'
    // "changes every render" note on this line is expected.
    // eslint-disable-next-line react-hooks/exhaustive-deps
    const formatter = new NumberFormatter(props.formatOptions ?? {});
    const [displayValue, setDisplayValue] = useState(
      () => formatter.format(field.value).formatted,
    );

    const handleBlur = useCallback(
      (e: React.FocusEvent<HTMLInputElement>) => {
        isKeystrokeRef.current = false; // reset user editing state
        field.onBlur(); // for RHF validation
        props.onBlur?.(e);
      },
      [field],
    );

    // A plain function, not a useCallback: it closes over the per-render
    // `formatter`, whose identity changes every render by design, so memoising it
    // would either churn the callback or pin a stale formatter.
    const handleOnChange = (e: React.ChangeEvent<HTMLInputElement>) => {
      isKeystrokeRef.current = true;
      setDisplayValue(e.currentTarget.value ?? ''); // update display value
      props.onChange?.(e);

      const unformatted = formatter.unformat(e.currentTarget.value);
      field.onChange(unformatted ?? ''); // unformat the value for RHF
    };

    const handleValueChange = useCallback(
      (_: number | string | undefined, eventType: EventType) => {
        // Should allow the effect the kick in
        // This is to avoid some stupid re-render where the change event
        // is triggered on each keystroke and causes the field to go bananas
        if (NonKeystrokeEvents.includes(eventType)) {
          isKeystrokeRef.current = false;
        }
      },
      [],
    );

    useEffect(() => {
      // Update the display value whenever the field value changes (e.g. form
      // reset) or the format switches, unless the user is mid-edit.
      if (!isKeystrokeRef.current) {
        const { formatted } = formatter.format(field.value?.toString());
        setDisplayValue(formatted);
      }
    }, [field.value, formatter]);

    return (
      <>
        <NumberField
          {...inputProps}
          required={!!rules?.required}
          ref={mergedRefs}
          onChange={handleOnChange}
          onBlur={handleBlur}
          onValueChange={handleValueChange}
          value={displayValue}
          errorMessage={fieldState.error?.message?.toString()}
        />
        <input type="hidden" name={field.name} value={field.value ?? ''} />
      </>
    );
  };
};
